import { Select as SelectPrimitive } from 'radix-ui';
import { forwardRef, useId, type ButtonHTMLAttributes } from 'react';

import { useFieldControl } from './Field.js';
import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SELECT — a listbox for a KNOWN, SHORT set of options. Not a filter.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Follows `Button.tsx`'s five rules; only the things specific to a select are argued here.
 * If the list is long enough that a user would want to type at it — an extension picker, a DID
 * picker — this is the wrong primitive and `Combobox` is the right one.
 *
 * ── WHY RADIX AND NOT A NATIVE `<select>` ──────────────────────────────────
 * A native `<select>` is more accessible than almost anything hand-built, and it is the right
 * answer whenever a bare list of strings will do. It is used here for a reason that is not
 * cosmetic: its `<option>` renders as an OS widget that cannot contain the selected-state check
 * mark, cannot show a grouped-and-described row, and on Android renders as a modal list that
 * ignores the page's theme entirely. Radix rebuilds the widget in the page — which means it
 * also has to rebuild the keyboard contract (type-ahead, Home/End, PageUp/PageDown, Escape),
 * and that is exactly the part not to hand-roll. Everything below the API surface is Radix's;
 * this file adds tokens, a name, and two corrections.
 *
 * ── CORRECTION 1: THE POPUP LISTBOX HAS NO NAME OF ITS OWN ─────────────────
 * `Select.Content` renders `role="listbox"` and Radix gives it no accessible name, because it
 * cannot know one. A screen-reader user who lands in it hears "list box" with nothing saying
 * which control they opened, so the label that names the trigger is wired to the content too.
 *
 * Worth knowing: **axe will not tell you about this one.** `aria-input-field-name` explicitly
 * exempts a listbox that is a combobox's popup (`isComboboxPopup`), so the unnamed version is
 * clean in the automated suite — which is exactly how it ships. `Select.test.tsx` asserts the
 * listbox's accessible name directly for that reason.
 *
 * ── CORRECTION 2: `aria-labelledby` INCLUDES THE VALUE, DELIBERATELY ───────
 * A native `<select>` exposes its label as the NAME and the chosen option as the VALUE; a
 * screen reader reads both. A `role="combobox"` button has no separate value property — its
 * value is its content — so pointing `aria-labelledby` at the visible label alone (the obvious
 * implementation, and what most examples show) replaces the name and DROPS the current
 * selection from what gets announced. The user then hears "Codec, combo box" and has no way to
 * know it says G.711 without moving into the popup. Referencing the label AND the value node
 * gives "Codec G.711, combo box", which is what the native control would have said.
 *
 * This has to hold for `aria-label` too, and `aria-label` is a string with nothing to compose
 * onto. So a caller who names this with `aria-label` and no visible label gets that string
 * rendered into an `sr-only` node, which is then referenced exactly like a real `<label>`.
 * Otherwise the correction would apply to one of the two supported ways of naming the control
 * and quietly not to the other.
 *
 * ── ON THE EMPTY LIST ──────────────────────────────────────────────────────
 * An options array can legitimately be empty while a fetch is in flight. Rendering an empty
 * `role="listbox"` is a real defect and not just a lint one — it is a popup that announces
 * nothing at all — so the empty state renders a single `aria-disabled` option carrying the
 * message. It is not a Radix `Item`, so it is invisible to type-ahead and to arrow navigation,
 * which is correct: it is a message, not a choice.
 */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectOptionGroup {
  label: string;
  options: readonly SelectOption[];
}

export type SelectItem = SelectOption | SelectOptionGroup;

export type SelectSize = 'sm' | 'md' | 'lg';

const isGroup = (item: SelectItem): item is SelectOptionGroup => 'options' in item;

/**
 * `min-h` and not `h`, for the same two reasons as `Button`: WCAG 2.5.8 wants at least 24×24
 * CSS px of target (the smallest here is 32), and 1.4.4 wants the control to grow rather than
 * clip when the user scales text.
 */
const SIZES: Record<SelectSize, string> = {
  sm: 'min-h-8 px-2.5 py-1 text-sm gap-2',
  md: 'min-h-10 px-3 py-2 text-sm gap-2',
  lg: 'min-h-12 px-4 py-2.5 text-base gap-2',
};

export interface SelectProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'value' | 'defaultValue' | 'onChange' | 'children'
  > {
  /** Flat options, or groups of them — `{ label, options }` is rendered as a `role="group"`. */
  options: readonly SelectItem[];
  /** Controlled selection. Omit for uncontrolled; pair with `onValueChange`. */
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * Renders a visible `<label>` and wires it to both the trigger and the popup listbox. If it
   * is omitted the caller MUST pass `aria-label` or `aria-labelledby` — an unnamed select is
   * a control a screen-reader user cannot identify, and no default here could be honest.
   */
  label?: string;
  /** Shown, and announced as the value, until something is chosen. */
  placeholder?: string;
  size?: SelectSize;
  /** Sets `aria-invalid` and colours the boundary. Pair it with a `aria-describedby` message. */
  invalid?: boolean;
  /** Renders the hidden native `<select>` Radix uses for real form submission. */
  name?: string;
  required?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Rendered as a disabled option when `options` is empty. See the header. */
  emptyMessage?: string;
  /** Class for the label+trigger wrapper. `className` itself lands on the trigger. */
  containerClassName?: string;
}

const ChevronIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" focusable="false" aria-hidden="true">
    <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" focusable="false" aria-hidden="true">
    <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    value,
    defaultValue,
    onValueChange,
    label,
    placeholder = 'Select…',
    size = 'md',
    invalid = false,
    name,
    form,
    required = false,
    disabled = false,
    open,
    defaultOpen,
    onOpenChange,
    emptyMessage = 'No options available',
    containerClassName,
    className,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...rest
  },
  ref,
) {
  /**
   * ═══════════════════════════════════════════════════════════════════════════
   * IT COMPOSES WITH `Field`, AND IT DID NOT UNTIL WP6's FIRST FORM USED IT
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * `Input` reads `useFieldControl()`; this did not. So `<Field label="Phone type"><Select
   * options={…} /></Field>` — the obvious spelling, and the one every other control in the
   * system takes — produced a trigger with **NO ACCESSIBLE NAME AT ALL**. The Field rendered
   * its `<label for>` pointing at an id nothing owned, and the Select, having no `label` prop
   * of its own, rendered none either.
   *
   * axe caught it as a `critical` `button-name` violation the first time a page put the two
   * together, which is the system working — but the defect was two composition models for two
   * form controls, and that belongs here rather than in a rule about which spelling to use.
   *
   * Inside a Field the Field owns the label, the description, `aria-invalid` and `required`;
   * this renders no label of its own and inherits all four. Outside one, everything below
   * behaves exactly as it did.
   */
  const field = useFieldControl();

  const uid = useId();
  // The Field's id WINS over a caller-supplied one, for the reason `Input` gives: the
  // `<label for>` was committed to the Field's id before this component rendered.
  const triggerId = field?.id ?? id ?? `${uid}-trigger`;
  const labelId = `${uid}-label`;
  const valueId = `${uid}-value`;

  /** A Field already renders the visible label; rendering a second one would duplicate it. */
  const ownsLabel = field === null && label !== undefined;
  const describedBy = field?.['aria-describedby'];
  const isInvalid = invalid || field?.['aria-invalid'] === true;
  const isRequired = required || field?.required === true;

  /**
   * A caller who names this with `aria-label` instead of a visible `label` would otherwise lose
   * CORRECTION 2 entirely: `aria-label` is a STRING, and there is no way to compose the value
   * node onto a string — the name would be "Codec" and the selection would go unspoken, which
   * is the exact defect that correction exists to fix. So the string is parked in an `sr-only`
   * node and referenced like any other label. Only when nothing else names the control, because
   * a real `<label>` or the caller's own `aria-labelledby` is always the better referent.
   */
  const synthesiseLabelNode =
    field === null && label === undefined && ariaLabelledBy === undefined && ariaLabel !== undefined;

  // The ids that name this control, in the order they should be read. `label` and a caller's
  // `aria-labelledby` compose rather than compete — a field can legitimately be named by both
  // its own label and a section heading.
  const nameRefs = [
    ariaLabelledBy,
    /**
     * Inside a Field the name comes from the Field's own `<label for={field.id}>`, which the
     * browser associates with the trigger natively — so there is no id to reference here and
     * adding one would point at a label this component did not render.
     */
    field !== null ? undefined : label === undefined && !synthesiseLabelNode ? undefined : labelId,
  ].filter((candidate): candidate is string => candidate !== undefined);

  // See CORRECTION 2 in the header: the value node joins the name so the current selection is
  // actually spoken. When nothing names this by reference, the trigger's own text content is
  // the name, which already includes the value — so there is nothing to compose.
  const triggerLabelledBy =
    field !== null
      ? // CORRECTION 2 still applies inside a Field: the Field's label names the control, and
        // the value node is appended so the current selection is actually spoken rather than
        // silently implied. `field.labelId` exists for exactly this — see `FieldControlAria`.
        [field.labelId, valueId].join(' ')
      : nameRefs.length > 0
        ? [...nameRefs, valueId].join(' ')
        : undefined;
  // See CORRECTION 1. The last resort is a generic string rather than nothing, because
  // `aria-label` on the trigger is not inherited by the popup.
  const listboxLabelledBy = nameRefs.length > 0 ? nameRefs.join(' ') : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {ownsLabel ? (
        <label id={labelId} htmlFor={triggerId} className="text-sm font-medium text-fg">
          {label}
        </label>
      ) : synthesiseLabelNode ? (
        // Not visible, and not meant to be — see `synthesiseLabelNode` above. `sr-only` clips
        // it rather than hiding it, so it stays in the accessibility tree where it is needed.
        <span id={labelId} className="sr-only">
          {ariaLabel}
        </span>
      ) : null}

      {/**
       * `exactOptionalPropertyTypes` is why these are spread conditionally instead of passed as
       * `value={value}`. Radix declares `value?: string`, which under this tsconfig REJECTS an
       * explicit `undefined` — and it has to, because `value={undefined}` is how a controlled
       * component silently becomes uncontrolled. Omitting the prop is the difference.
       */}
      <SelectPrimitive.Root
        {...(value === undefined ? {} : { value })}
        {...(defaultValue === undefined ? {} : { defaultValue })}
        {...(onValueChange === undefined ? {} : { onValueChange })}
        {...(name === undefined ? {} : { name })}
        {...(form === undefined ? {} : { form })}
        {...(open === undefined ? {} : { open })}
        {...(defaultOpen === undefined ? {} : { defaultOpen })}
        {...(onOpenChange === undefined ? {} : { onOpenChange })}
        disabled={disabled}
        required={isRequired}
      >
        <SelectPrimitive.Trigger
          ref={ref}
          id={triggerId}
          {...(triggerLabelledBy === undefined ? {} : { 'aria-labelledby': triggerLabelledBy })}
          /* Dropped when the same string is already doing the job as a referenced node:
             `aria-labelledby` outranks `aria-label`, so leaving both would put an attribute in
             the DOM that reads as the name and is not. */
          {...(ariaLabel === undefined || synthesiseLabelNode ? {} : { 'aria-label': ariaLabel })}
          aria-invalid={isInvalid || undefined}
          {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
          className={cn(
            'inline-flex items-center justify-between rounded-md border font-normal',
            'bg-surface text-fg transition-colors duration-150',
            // No `outline-none` anywhere in this file: `:focus-visible` in styles.css is the
            // one focus indicator and every primitive inherits it rather than restating it.
            'hover:bg-surface-sunken',
            // The placeholder is dimmer than a real value — but it is still text held to 4.5:1,
            // which is why it is `fg-muted` and not an opacity.
            'data-[placeholder]:text-fg-muted',
            'disabled:pointer-events-none disabled:opacity-50',
            isInvalid ? 'border-danger' : 'border-border-strong',
            SIZES[size],
            className,
          )}
          {...rest}
        >
          <SelectPrimitive.Value id={valueId} placeholder={placeholder} />
          <SelectPrimitive.Icon className="shrink-0 text-fg-muted">
            <ChevronIcon />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            /* The fallback is a generic string and not `ariaLabel`: by the time this branch is
               reached the caller has supplied no name at all, because an `aria-label` on its
               own has already become a referenced node above. */
            {...(listboxLabelledBy === undefined
              ? { 'aria-label': 'Options' }
              : { 'aria-labelledby': listboxLabelledBy })}
            /**
             * `popper` rather than Radix's default `item-aligned`. Item-aligned reproduces the
             * macOS habit of overlaying the selected row on top of the trigger, which requires
             * measuring the item, the viewport and the trigger and then repositioning — it
             * fights text zoom, it has nothing to measure before first paint, and when the list
             * is taller than the viewport it is the mode that clips. Anchored below the trigger
             * is boring and always right.
             */
            position="popper"
            sideOffset={4}
            className={cn(
              'z-popover max-h-80 min-w-[var(--radix-select-trigger-width)] overflow-hidden',
              'rounded-md border border-border bg-surface-raised text-fg shadow-lg',
            )}
          >
            <SelectPrimitive.Viewport className="p-1">
              {options.length === 0 ? (
                /**
                 * A message shaped like an option, so the listbox is never empty. See header.
                 */
                <div
                  role="option"
                  aria-disabled="true"
                  aria-selected="false"
                  className="px-2 py-1.5 pl-8 text-sm text-fg-muted"
                >
                  {emptyMessage}
                </div>
              ) : (
                options.map((item, index) =>
                  isGroup(item) ? (
                    <SelectPrimitive.Group key={`group-${String(index)}`}>
                      {/* Radix points the group's `aria-labelledby` at this node, so the group
                          heading is announced when a user arrows into it rather than being a
                          purely visual divider. */}
                      <SelectPrimitive.Label className="px-2 py-1.5 text-xs font-medium text-fg-muted">
                        {item.label}
                      </SelectPrimitive.Label>
                      {item.options.map((option) => (
                        <Option key={option.value} option={option} />
                      ))}
                    </SelectPrimitive.Group>
                  ) : (
                    <Option key={item.value} option={item} />
                  ),
                )
              )}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
});

function Option({ option }: { option: SelectOption }) {
  return (
    <SelectPrimitive.Item
      value={option.value}
      disabled={option.disabled ?? false}
      className={cn(
        'relative flex cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm select-none',
        // Radix moves REAL focus onto the highlighted item, so the global `:focus-visible` ring
        // is drawn for a keyboard user and suppressed for a mouse user, for free. This
        // background is the pointer affordance, not the focus indicator.
        'data-[highlighted]:bg-accent-subtle',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      )}
    >
      {/* The check is decoration: `aria-selected` on the item is what carries the state, and
          announcing "tick" on top of "selected" is noise. */}
      <SelectPrimitive.ItemIndicator
        aria-hidden="true"
        className="absolute left-2 inline-flex items-center text-accent"
      >
        <CheckIcon />
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}
